import React, { useState } from "react";
import { Button, Col, Form, Input, notification, Row } from "antd";
import AuthService from "../../base/services/authentication.service";
import { httpService } from "../../base/services/httpService.service";
import VerificationCodeModal from "../VerificationCodeModal/VerificationCodeModal";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import "../VerificationCodeModal/VerificationCodeModal.scss";

const UpdatePassword = () => {
   const userId = AuthService.getUser()?.id;
   const userEmail = AuthService.getUser()?.email;
   const [form] = Form.useForm();
   const [isLoading, setIsLoading] = useState(false);
   const [validateConfirmationCodeModal, setValidateConfirmationCodeModal] = useState(false);
   const { executeRecaptcha } = useGoogleReCaptcha();

   const onFinish = async (values) => {
      if(!executeRecaptcha) {
         return;
      }   

      setIsLoading(true);

      const recaptchaToken = await executeRecaptcha('login');
      httpService
         .post(
            "/Account/forgotPassword",
            {
               email: userEmail
            },
            (response) => {
               if (response?.isSuccess) {
                  setValidateConfirmationCodeModal(true);
                  notification.success({
                     message: "A verification code has been sent to your email."
                  });
               } else if (response?.errors) {
                  notification.error({
                     message: "There was an error updating the password. Please try again."
                  });
               }
            },
            (error) => {
               notification.error({
                  message:
                     (Array.isArray(error.response.data.errors) &&
                        error.response.data.errors.join(", ")) ||
                     "Something went wrong. Please try again."
               });
            },
            undefined,
            {
               'g-recaptcha-response': recaptchaToken
            }
         )
         .finally(() => {
            setIsLoading(false);
         });
   };

   const onComplete = async (code) => {
      if(!executeRecaptcha) {
         return;
      }
      setIsLoading(true);

      const recaptchaToken = await executeRecaptcha('login');
      httpService
         .post(
            "/Account/checkResetPasswordCode",
            {
               userId,
               code
            },
            (response) => {
               if (response?.isSuccess) {
                  httpService
                     .post(
                        "/account/changePassword",
                        {
                           oldPassword: form.getFieldValue("oldPassword"),
                           newPassword: form.getFieldValue("newPassword"),
                           confirmPassword: form.getFieldValue("confirmPassword")
                        },
                        (response) => {
                           if (response?.isSuccess) {
                              setValidateConfirmationCodeModal(false);
                              notification.success({
                                 message: "Your password has been updated successfully."
                              });
                              AuthService.logout();
                           }
                        }
                     )
                     .catch((error) => {
                        notification.error({
                           message:
                              (Array.isArray(error.response.data.errors) &&
                                 error.response.data.errors.join(", ")) ||
                              "There was an error updating the password. Please try again."
                        });
                     })
                     .finally(() => {
                        setIsLoading(false);
                     });
               }
            },
            () => {},
            undefined,
            {
               'g-recaptcha-response': recaptchaToken
            }
         )
         .catch((error) => {
            notification.error({
               message:
                  (Array.isArray(error.response.data.errors) &&
                     error.response.data.errors.join(", ")) ||
                  "There was an error updating the password. Please try again."
            });
            setIsLoading(false);
         });
   };

   const onCancel = () => {
      setIsLoading(false);
      setValidateConfirmationCodeModal(false);
   };

   return (
      <>
         <Form form={form} onFinish={onFinish} autoComplete="off" layout="vertical">
            <div className="mt-5 password-list">
               <span className="password-list-text">Your new password must contain at least:</span>
               <ul>
                  <li className="password-list-text">One uppercase character</li>
                  <li className="password-list-text">One lowercase character</li>
                  <li className="password-list-text">One number</li>
               </ul>
            </div>

            <Row gutter={24}>
               <Col xs={24} sm={12}>
                  <Form.Item
                     label={<span className="label-text">Old password</span>}
                     name="oldPassword"
                     rules={[{ required: true, message: "Please enter your old password!" }]}
                  >
                     <Input.Password
                        autoComplete="new-password"
                        placeholder="Type your new password"
                     />
                  </Form.Item>
               </Col>
               <Col xs={24} sm={12}>
                  <Form.Item
                     label={<span className="label-text">New password</span>}
                     name="newPassword"
                     rules={[{ required: true, message: "Please enter your new password!" }]}
                  >
                     <Input.Password autoComplete="off" placeholder="Type your new password" />
                  </Form.Item>
               </Col>
               <Col xs={24} sm={12}>
                  <Form.Item
                     label={<span className="label-text">Confirm new Password</span>}
                     name="confirmPassword"
                     dependencies={["newPassword"]}
                     rules={[
                        {
                           required: true,
                           message: "Please confirm your password!"
                        },
                        ({ getFieldValue }) => ({
                           validator(_, value) {
                              if (!value || getFieldValue("newPassword") === value) {
                                 return Promise.resolve();
                              }
                              return Promise.reject(new Error("The two passwords do not match!"));
                           }
                        })
                     ]}
                  >
                     <Input.Password autoComplete="off" placeholder="Confirm your new password" />
                  </Form.Item>
               </Col>
            </Row>

            <Row gutter={16} className="mt-8" align="middle">
               <Col>
                  <Button htmlType="submit" className="primaryButton" loading={isLoading}>
                     Update
                  </Button>
               </Col>
            </Row>
         </Form>

         {validateConfirmationCodeModal && (
            <VerificationCodeModal
               isVisible={validateConfirmationCodeModal}
               onComplete={onComplete}
               onCancel={onCancel}
               isLoading={isLoading}
               email={userEmail}
            >
               <hr />
               <p className="description">
                  If you haven't received the email, click{" "}
                  <strong onClick={() => onFinish(form.getFieldsValue())}>
                     Resend verification code
                  </strong>
               </p>
            </VerificationCodeModal>
         )}
      </>
   );
};

export default UpdatePassword;
